import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { User } from '../types'
import { authService, AuthenticationError } from '../services/authService'
import { Result } from '../utils/errors'
import { logger, logUserAction } from '../services/logger'
import { queryClient } from '../App'

// Authentication State Interface
interface AuthState {
  // State
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  loginAttempts: number
  isLocked: boolean
  lockoutUntil: number | null

  // Actions
  login: (email: string, password: string) => Promise<Result<User, Error>>
  logout: () => void
  clearError: () => void
  resetLockout: () => void
  updateProfile: (updates: Partial<User>) => Promise<Result<User, Error>>

  // New AWS Cognito specific actions
  signUp: (
    email: string,
    password: string,
    name: string
  ) => Promise<Result<{ user: User | null; nextStep: string }, Error>>
  confirmSignUp: (
    email: string,
    confirmationCode: string,
    password: string
  ) => Promise<Result<void, Error>>
  resetPassword: (email: string) => Promise<Result<void, Error>>
  confirmPasswordReset: (
    email: string,
    confirmationCode: string,
    newPassword: string
  ) => Promise<Result<void, Error>>
  changePassword: (oldPassword: string, newPassword: string) => Promise<Result<void, Error>>
  resendConfirmationCode: (email: string) => Promise<Result<void, Error>>

  // Internal methods
  _setLoading: (loading: boolean) => void
  _setError: (error: string | null) => void
  _incrementLoginAttempts: () => void
  _resetLoginAttempts: () => void
  _setLockout: () => void
  _checkCurrentUser: () => Promise<void>
}

// Create the store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      loginAttempts: 0,
      isLocked: false,
      lockoutUntil: null,

      // Actions
      login: async (email: string, password: string): Promise<Result<User, Error>> => {
        const state = get()

        // Check if account is locked
        if (state.isLocked && state.lockoutUntil && Date.now() < state.lockoutUntil) {
          const remainingTime = Math.ceil((state.lockoutUntil - Date.now()) / 1000 / 60)
          const error = new AuthenticationError(
            `Account is locked. Try again in ${remainingTime} minutes.`
          )
          state._setError(error.message)
          return Result.err(error)
        }

        // Reset lockout if time has passed
        if (state.isLocked && state.lockoutUntil && Date.now() >= state.lockoutUntil) {
          state.resetLockout()
        }

        // state._setLoading(true)
        state._setError(null)

        try {
          logger.info('Login attempt started', { email })

          // Use AWS Cognito authentication
          const user = await authService.signIn(email, password)

          // Update auth state
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginAttempts: 0,
            isLocked: false,
            lockoutUntil: null,
          })

          // Clear all React Query cache on successful login to ensure fresh data
          logger.info('Clearing React Query cache on login', { userId: user.id })
          queryClient.clear()
          await queryClient.invalidateQueries()

          logUserAction('login_success', { userId: user.id, userRole: user.role })
          logger.info('Login successful', { userId: user.id, email: user.email })

          return Result.ok(user)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Login failed')

          // Increment login attempts
          state._incrementLoginAttempts()

          // Lock account after max attempts (5)
          if (state.loginAttempts >= 99) {
            state._setLockout()
            logger.warn('Account locked due to too many failed attempts', { email })
          }

          state._setError(authError.message)
          state._setLoading(false)

          logUserAction('login_failed', { email, error: authError.message })
          logger.error('Login failed', authError, { email })

          return Result.err(authError)
        }
      },

      signUp: async (
        email: string,
        password: string,
        name: string
      ): Promise<Result<{ user: User | null; nextStep: string }, Error>> => {
        const state = get()
        state._setLoading(true)
        state._setError(null)

        try {
          logger.info('Sign up attempt', { email, name })

          const result = await authService.signUp(email, password, name)

          state._setLoading(false)
          logger.info('Sign up initiated', { email, nextStep: result.nextStep })

          return Result.ok(result)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Sign up failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Sign up failed', authError, { email })
          return Result.err(authError)
        }
      },

      confirmSignUp: async (
        email: string,
        confirmationCode: string,
        password: string
      ): Promise<Result<void, Error>> => {
        const state = get()
        state._setLoading(false)
        state._setError(null)

        try {
          await authService.confirmSignUp(email, confirmationCode, password)

          state._setLoading(false)
          logger.info('Sign up confirmed', { email })

          return Result.ok(undefined)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Confirmation failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Sign up confirmation failed', authError, { email })
          return Result.err(authError)
        }
      },

      resetPassword: async (email: string): Promise<Result<void, Error>> => {
        const state = get()
        state._setLoading(false)
        state._setError(null)

        try {
          await authService.resetPassword(email)

          state._setLoading(false)
          logger.info('Password reset requested', { email })

          return Result.ok(undefined)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Password reset failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Password reset failed', authError, { email })
          return Result.err(authError)
        }
      },

      confirmPasswordReset: async (
        email: string,
        confirmationCode: string,
        newPassword: string
      ): Promise<Result<void, Error>> => {
        const state = get()
        state._setLoading(false)
        state._setError(null)

        try {
          await authService.confirmPasswordReset(email, confirmationCode, newPassword)

          state._setLoading(false)
          logger.info('Password reset confirmed', { email })

          return Result.ok(undefined)
        } catch (error) {
          const authError =
            error instanceof Error ? error : new Error('Password reset confirmation failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Password reset confirmation failed', authError, { email })
          return Result.err(authError)
        }
      },

      changePassword: async (
        oldPassword: string,
        newPassword: string
      ): Promise<Result<void, Error>> => {
        const state = get()
        state._setLoading(true)
        state._setError(null)

        try {
          await authService.changePassword(oldPassword, newPassword)

          state._setLoading(false)
          logger.info('Password changed successfully')

          return Result.ok(undefined)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Password change failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Password change failed', authError)
          return Result.err(authError)
        }
      },

      resendConfirmationCode: async (email: string): Promise<Result<void, Error>> => {
        const state = get()
        state._setLoading(false)
        state._setError(null)

        try {
          await authService.resendConfirmationCode(email)

          state._setLoading(false)
          logger.info('Confirmation code resent', { email })

          return Result.ok(undefined)
        } catch (error) {
          const authError =
            error instanceof Error ? error : new Error('Resend confirmation code failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Resend confirmation code failed', authError, { email })
          return Result.err(authError)
        }
      },

      logout: async () => {
        const { user } = get()

        try {
          if (user) {
            logUserAction('logout', { userId: user.id })
            logger.info('User logged out', { userId: user.id })
          }

          await authService.signOut()

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            loginAttempts: 0,
            isLocked: false,
            lockoutUntil: null,
          })

          // Clear all React Query cache on logout
          logger.info('Clearing React Query cache on logout')
          queryClient.clear()
        } catch (error) {
          logger.error('Logout failed', error instanceof Error ? error : new Error(String(error)))
          // Even if logout fails, clear local state and cache
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            loginAttempts: 0,
            isLocked: false,
            lockoutUntil: null,
          })

          // Clear cache even if logout API call fails
          logger.info('Clearing React Query cache on logout (after error)')
          queryClient.clear()
        }
      },

      updateProfile: async (updates: Partial<User>): Promise<Result<User, Error>> => {
        const state = get()
        state._setLoading(true)
        state._setError(null)

        try {
          const updatedUser = await authService.updateProfile(updates)

          set({
            user: updatedUser,
            isLoading: false,
          })

          logger.info('Profile updated successfully', { userId: updatedUser.id })
          return Result.ok(updatedUser)
        } catch (error) {
          const authError = error instanceof Error ? error : new Error('Profile update failed')

          state._setError(authError.message)
          state._setLoading(false)

          logger.error('Profile update failed', authError)
          return Result.err(authError)
        }
      },

      clearError: () => {
        set({ error: null })
      },

      resetLockout: () => {
        set({
          isLocked: false,
          lockoutUntil: null,
          loginAttempts: 0,
        })
      },

      // Internal methods
      _setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      _setError: (error: string | null) => {
        set({ error })
      },

      _incrementLoginAttempts: () => {
        set((state) => ({
          loginAttempts: state.loginAttempts + 1,
        }))
      },

      _resetLoginAttempts: () => {
        set({ loginAttempts: 0 })
      },

      _setLockout: () => {
        set({
          isLocked: true,
          lockoutUntil: Date.now() + 15 * 60 * 1000, // 15 minutes
        })
      },

      _checkCurrentUser: async () => {
        set({ isLoading: true })
        try {
          const user = await authService.getCurrentUser()
          if (user) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            })

            // Clear cache when user session is restored to ensure fresh data
            logger.info('Clearing React Query cache on session restore', { userId: user.id })
            queryClient.clear()
            await queryClient.invalidateQueries()
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            })

            // Clear cache when no user is found
            logger.info('Clearing React Query cache - no user session found')
            queryClient.clear()
          }
        } catch (error) {
          logger.debug('No current user found')
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })

          // Clear cache on auth check error
          logger.info('Clearing React Query cache on auth check error')
          queryClient.clear()
        }
      },
    }),
    {
      name: 'danone-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist non-sensitive data
        loginAttempts: state.loginAttempts,
        isLocked: state.isLocked,
        lockoutUntil: state.lockoutUntil,
      }),
    }
  )
)

// Initialize authentication state on app start
const initializeAuth = async () => {
  const store = useAuthStore.getState()
  await store._checkCurrentUser()
}

// Auto-initialize when store is created
setTimeout(initializeAuth, 100)

// Listen for auth state changes from the service
authService.addListener((user) => {
  const store = useAuthStore.getState()
  if (user) {
    useAuthStore.setState({
      user,
      isAuthenticated: true,
      isLoading: false,
    })
  } else {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    })
  }
})

// Selector hooks for components
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useAuthLoading = () => useAuthStore((state) => state.isLoading)
export const useAuthError = () => useAuthStore((state) => state.error)

// Action hooks
export const useLogin = () => useAuthStore((state) => state.login)
export const useSignUp = () => useAuthStore((state) => state.signUp)
export const useConfirmSignUp = () => useAuthStore((state) => state.confirmSignUp)
export const useLogout = () => useAuthStore((state) => state.logout)
export const useResetPassword = () => useAuthStore((state) => state.resetPassword)
export const useConfirmPasswordReset = () => useAuthStore((state) => state.confirmPasswordReset)
export const useChangePassword = () => useAuthStore((state) => state.changePassword)
export const useResendConfirmationCode = () => useAuthStore((state) => state.resendConfirmationCode)
export const useClearAuthError = () => useAuthStore((state) => state.clearError)
export const useUpdateProfile = () => useAuthStore((state) => state.updateProfile)
